import './index.css'
import 'pages/settings/index.css'

import cn from 'clsx'
import debounce from 'lodash/debounce'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Link, NavigateFunction, Outlet, useNavigate } from 'react-router-dom'

import { SORT_DIRECTION_ASC } from '@disco/constants/SortTypes'
import {
  addContactValue,
  changeContactValue,
  deleteContacts,
  downloadContacts,
  loadContactsIds,
  loadNextContacts,
  removeContactValue,
  removeWarning,
  resetSortContacts,
  searchContacts,
  sortContacts,
  switchContactsMode,
  toggleContacts,
  unsubscribeContacts,
  updateContactsTags,
} from '@disco/state-management/actions/contact-actions'
import { listFormStartManagingLists } from '@disco/state-management/actions/EmailGroupActions'
import {
  getContacts, // getContactsList,
  getContactsByIds,
  getSelectAllContactIds,
  getWarningContact,
} from '@disco/state-management/selectors/contacts'
import {
  getHideContactRemovalSetting,
  getUser,
} from '@disco/state-management/selectors/user'
import { getContactStatus } from '@disco/utils/contacts'

import type { Contact, RecipientList } from 'types/list'
import type { PopoverItemType } from 'types/ui'

import HelpTooltip from 'components/common/tooltip'
import DownloadModal from 'components/modals/download-modal'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Icon from 'components/ui/icon'
import Input from 'components/ui/input'
import ModalDialog from 'components/ui/modal-dialog'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Tooltip from 'components/ui/tooltip'
import UserAvatar from 'components/ui/user-avatar'

import {
  CAPTION_EXISTING,
  CAPTION_IMPORT,
  CAPTION_SINGLE,
} from '../add/buttons'
import UnsubscribeIcon from './contact-row/unsubscribe-icon'
import ContactsOptinModal from './contacts-optin-modal'
import ContactsTable from './contacts-table'
import ContactsTagList from './contacts-tag-list'

function mapStateToProps(state) {
  return {
    ...getContacts(state),
    selectAllIds: getSelectAllContactIds(state),
    listSaving: state.forms.listForm.saving,
    filteredContacts: getContactsByIds(state) as (Contact & {
      selected: boolean
      editMode: boolean
    })[],
    hideContactRemoval: getHideContactRemovalSetting(state),
    warningContact: getWarningContact(state),
    isAdmin: getUser(state)?.is_admin,
  }
}

const mapDispatchToProps = {
  loadContactsIds,
  loadNextContacts,
  sortContacts,
  listFormStartManagingLists,
  searchContacts,
  switchContactsMode,
  changeContactValue,
  addContactValue,
  removeContactValue,
  resetSortContacts,
  deleteContacts,
  toggleContacts,
  unsubscribeContacts,
  updateContactsTags,
  removeWarning,
  downloadContacts,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type ReduxProps = ConnectedProps<typeof connector>

type Props = ReduxProps & {
  children?: React.ReactNode
  list: RecipientList | null | undefined
  listSaving: boolean
  appendingContacts?: boolean
  isAdmin: boolean
  navigate: NavigateFunction
}

// Identifies which list/append-mode context a search term belongs to, so a
// term typed in one context (e.g. a specific mailing list) is never reused
// as if it were typed in another (e.g. after switching mailing lists) —
// only reused when actually returning to the same context, such as after
// viewing a contact's detail view and navigating back.
function getSearchContext(
  list: RecipientList | null | undefined,
  appendingContacts: boolean | undefined,
): string {
  return list ? `${list.id}-${Boolean(appendingContacts)}` : 'all'
}

function getInitialSearchTerm(props: Props): string {
  const contextMatches =
    props.searchContext ===
    getSearchContext(props.list, props.appendingContacts)
  return contextMatches ? props.searchTerm ?? '' : ''
}

/*
{
    listSaving: boolean
    appendingContacts?: boolean
    isAdmin: boolean
  },
*/

class ContactsList extends React.Component<
  Props,
  {
    appendPrompt: boolean
    searchTerm: string
    modalOpen: boolean
    downloadUrl: string | null | undefined
    deferred: boolean
    error: boolean
    loading: boolean
  }
> {
  state = {
    appendPrompt: false,
    searchTerm: getInitialSearchTerm(this.props),
    modalOpen: false,
    downloadUrl: null,
    deferred: false,
    error: false,
    loading: false,
  }

  componentDidMount() {
    const { loading, loadContactsIds, list, appendingContacts } = this.props
    const searchTerm = this.state.searchTerm
    const searchContext = getSearchContext(list, appendingContacts)

    if (this.props.searchContext !== searchContext) {
      this.props.searchContacts(searchTerm, searchContext)
    }

    if (!loading) {
      if (list && !appendingContacts) {
        loadContactsIds(searchTerm, 'name', list.id)
      } else {
        loadContactsIds(searchTerm, 'name')
      }
    }

    this._deselectAll()

    this.props.resetSortContacts()
  }

  _search = debounce(() => {
    const { list, appendingContacts, loadContactsIds } = this.props
    this.props.searchContacts(
      this.state.searchTerm,
      getSearchContext(list, appendingContacts),
    )

    if (list && !appendingContacts) {
      loadContactsIds(this.state.searchTerm, 'name', list.id)
    } else {
      loadContactsIds(this.state.searchTerm, 'name')
    }
  }, 400)
  _onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      searchTerm: e.target.value,
    })

    this._search()
  }
  _onRowModeSwitch = (contact: Contact) => {
    const clickOnSelectedContact = this.props.filteredContacts.find(
      c => c.id === contact.id && c.selected && !c.editMode,
    )

    if (clickOnSelectedContact) {
      const { selectedContactIds } = this._getSelectedContacts()
      this.props.switchContactsMode(selectedContactIds, true, true)
    } else {
      // savedRecipientsId only matters here: this is the save path (see
      // switchContactsMode), entering edit mode never reads it
      this.props.switchContactsMode([contact.id], false, undefined, {
        savedRecipientsId: this.props.list?.id,
      })
    }
  }
  _onFieldChange = (contact: Contact, field: string, value: string) => {
    this.props.changeContactValue(contact.id, field, value)
  }
  _onFieldValueAdd = (contact: Contact, field: string, value: string) =>
    this.props.addContactValue(contact.id, field, value)
  _onFieldValueRemove = (contact: Contact, field: string, value: string) =>
    this.props.removeContactValue(contact.id, field, value)
  _onSelect = (contact: Contact) => {
    // adds contact to selection
    // equivalent to:
    //   this.props.toggleContacts([...selectedIds, contact.id], true)
    this.props.toggleContacts([contact.id])
  }
  _selectAll = () => {
    let contacts = this.props.filteredContacts
    const listId = this.props.list && this.props.list.id
    let ids = this.props.ids

    if (this.props.appendingContacts) {
      ids = contacts
        .filter(c => getContactStatus(c, listId) === '0_SUBSCRIBED')
        .map(c => c.id)
    }

    this.props.toggleContacts(ids, true)
  }
  _deselectAll = () => {
    this.props.toggleContacts(this.props.ids, true, true)
  }
  _exportClick = async (ids?: number[]) => {
    this.setState({
      loading: true,
      error: false,
      deferred: false,
      downloadUrl: null,
    })

    this._openModal()

    const result: Record<string, any> = await this.props.downloadContacts(ids)

    if (result?.deferred) {
      this.setState({
        deferred: true,
        loading: false,
      })
    }

    if (result?.download_url) {
      this.setState({
        downloadUrl: result?.download_url,
        loading: false,
      })
    }

    if (result?.error) {
      this.setState({
        error: true,
        loading: false,
      })
    }
  }
  _openModal = () =>
    this.setState({
      modalOpen: true,
    })
  _closeModal = () =>
    this.setState({
      modalOpen: false,
      downloadUrl: null,
      error: false,
      deferred: false,
    })

  _renderModal() {
    if (!this.state.modalOpen) return null

    if (this.state.downloadUrl) {
      return (
        <DownloadModal
          type="base"
          onClose={this._closeModal}
          url={this.state.downloadUrl}
        />
      )
    }

    if (this.state.loading) {
      return (
        <DownloadModal
          type="loading"
          onClose={this._closeModal}
          url={this.state.downloadUrl}
        />
      )
    }

    if (this.state.error) {
      return (
        <DownloadModal
          type="error"
          onClose={this._closeModal}
          onClick={() => {
            const { selectedContactIds } = this._getSelectedContacts()

            this._exportClick(selectedContactIds)
          }}
        />
      )
    }

    if (this.state.deferred) {
      return <DownloadModal type="deferred" onClose={this._closeModal} />
    }
  }

  _getSelectedContacts = () => {
    const selectedContacts = this.props.filteredContacts.filter(c => c.selected)
    const selectedContactIds = this.props.selectedAll
      ? this.props.selectAllIds
      : selectedContacts.map(c => c.id)
    return {
      selectedContacts,
      selectedContactIds,
    }
  }
  _onActionClick = (
    action:
      | 'Delete'
      | 'EditList'
      | 'DeleteList'
      | 'ChangeLists'
      | 'Edit'
      | 'Unsubscribe'
      | 'Export',
  ) => {
    const { selectedContacts, selectedContactIds } = this._getSelectedContacts()
    const { list } = this.props

    if (action === 'EditList' && list) {
      return this.props.navigate(`/lists/${list.id}/edit`)
    } else if (action === 'DeleteList' && list) {
      return this.props.navigate(`/lists/${list.id}/delete-list`)
    }

    // guard on the effective selection: with select-all active it can cover
    // only not-yet-loaded contacts, leaving zero selected rows hydrated
    if (!selectedContactIds.length) return

    switch (action) {
      case 'Delete':
        if (this.props.hideContactRemoval) {
          this.props.deleteContacts(selectedContactIds)
        } else {
          if (list) {
            this.props.navigate(`/lists/${list.id}/delete`)
          } else {
            this.props.navigate('/contacts/list/delete')
          }
        }

        break

      case 'ChangeLists':
        this.props.listFormStartManagingLists(
          selectedContacts,
          'MULTIPLE_LISTS',
          list,
          {},
          selectedContactIds,
        )
        break

      case 'Edit':
        this.props.switchContactsMode(selectedContactIds, true, true)
        break

      case 'Unsubscribe':
        this.props.unsubscribeContacts(
          selectedContactIds,
          list ? list.id : null,
        )
        break

      case 'Export':
        this._exportClick(selectedContactIds)

        break
    }
  }
  _handleAppendPromptClick = () => {
    this.setState({
      appendPrompt: true,
    })
  }
  _handleAppendToListClick = async () => {
    const { selectedContacts, selectedContactIds } = this._getSelectedContacts()
    const { list } = this.props
    if (!list) return
    await this.props.listFormStartManagingLists(
      selectedContacts,
      'EXISTING_LIST',
      list,
      {
        redirectTo: `/lists/${list.id}`,
      },
      selectedContactIds,
    )
    this.setState({
      appendPrompt: false,
    })
  }
  _handleAppendCancel = () => {
    this.setState({
      appendPrompt: false,
    })
  }

  _menuOptions() {
    const { list, appendingContacts } = this.props
    const selectedContacts = this.props.filteredContacts.filter(c => c.selected)
    const nonListContact = selectedContacts.find(t => !t.email)
    let changeListItem: PopoverItemType = {
      value: 'ChangeLists',
      label: 'Add to / remove from lists',
      disabled: false,
    }

    if (nonListContact) {
      changeListItem.nodeRight = (
        <Tooltip
          content={
            <>
              Contacts without email, like <b>“{nonListContact.name}”</b>, could
              not be added.
            </>
          }
        >
          <Icon type="info-outline" width={16} height={16} />
        </Tooltip>
      )
      changeListItem.disabled = true
    }

    let unsubscribeItem = {
      value: 'Unsubscribe',
      label:
        selectedContacts.length === 1
          ? 'Unsubscribe user'
          : 'Unsubscribe users',
    }
    const editSelectedItem = {
      value: 'Edit',
      label: 'Edit selected',
    }
    const exportSelected = {
      value: 'Export',
      label: 'Export selected to CSV',
      disabled: !this.props.isAdmin,
      nodeRight: !this.props.isAdmin && (
        <Tooltip content="Only admins can export contacts.">
          <Icon type="info-outline" width={16} height={16} />
        </Tooltip>
      ),
    }

    if (list) {
      if (appendingContacts) {
        return []
      } else {
        return [
          editSelectedItem,
          changeListItem,
          unsubscribeItem,
          exportSelected,
        ]
      }
    } else {
      return [
        editSelectedItem,
        changeListItem,
        unsubscribeItem,
        exportSelected,
        {
          value: 'Delete',
          label: 'Delete selected',
          destructive: true,
        },
      ]
    }
  }

  _otherMenuOptions() {
    return [
      {
        value: 'EditList',
        label: 'Edit List',
      },
      {
        value: 'DeleteList',
        label: 'Delete List',
        destructive: true,
      },
    ]
  }

  _contactOptions() {
    return [
      {
        value: '/contacts/list/file-import',
        label: CAPTION_IMPORT,
      },
      {
        value: '/contacts/list/add-single',
        label: CAPTION_SINGLE,
      },
    ]
  }

  _recipientOptions(id) {
    return [
      {
        value: `/contacts/list?append=${id}`,
        label: CAPTION_EXISTING,
      },
      {
        value: `/lists/${id}/file-import`,
        label: CAPTION_IMPORT,
      },
      {
        value: `/lists/${id}/add-single`,
        label: CAPTION_SINGLE,
      },
    ]
  }

  _onAddNewContact = (url: string) => {
    this.props.navigate(url)
  }
  _sortContacts = debounce((value: string) => {
    const sortQuery = `${
      this.props.sortParam === value
        ? this.props.sortDirection === SORT_DIRECTION_ASC
          ? '-'
          : ''
        : ''
    }${value.toLowerCase()}`
    this.props.sortContacts(value)

    if (this.props.list && !this.props.appendingContacts) {
      this.props.loadContactsIds(
        this.state.searchTerm,
        sortQuery,
        this.props.list.id,
      )
    } else {
      this.props.loadContactsIds(this.state.searchTerm, sortQuery)
    }
  }, 300)

  render() {
    const {
      filteredContacts,
      sortDirection,
      sortParam,
      searchTerm,
      loading,
      appendingContacts,
      list,
      updateContactsTags,
      loadNextContacts,
      ids,
      selectedAll,
      selectAllIds,
      warningContact,
      loaded,
      error,
    } = this.props
    const selectedNum = selectedAll
      ? selectAllIds.length
      : filteredContacts.filter(c => c.selected).length
    let searchPlaceholder
    let addBtn
    let selectionMenu
    let listMenu
    let optinModal
    let enableSearch = true

    if (list) {
      if (appendingContacts) {
        searchPlaceholder = 'Search contacts...'
        addBtn = (
          <Button
            appearance="primary"
            caption="Add to list"
            progressCaption="Adding to list"
            progressIndicator={this.props.listSaving}
            onClick={this._handleAppendPromptClick}
            disabled={selectedNum === 0}
          />
        )

        if (this.state.appendPrompt) {
          optinModal = (
            <ContactsOptinModal
              saving={this.props.listSaving}
              onSave={this._handleAppendToListClick}
              onCancel={this._handleAppendCancel}
            />
          )
        }
      } else {
        searchPlaceholder = 'Search mailing list...'

        if (filteredContacts.length > 0 || searchTerm) {
          addBtn = (
            <EllipsisMenu
              behavior="button-dropdown"
              compact
              options={this._recipientOptions(list.id)}
              onOptionClick={this._onAddNewContact}
            />
          )
        } else {
          enableSearch = false
        }

        listMenu = (
          <EllipsisMenu
            compact
            hasArrow={false}
            icon="ellipsis"
            options={this._otherMenuOptions()}
            onOptionClick={this._onActionClick}
          />
        )
      }
    } else {
      searchPlaceholder = 'Search contacts...'
      addBtn = (
        <Button
          appearance="primary"
          caption={CAPTION_SINGLE}
          link="/contacts/list/add-single"
        />
      )
    }

    if (!appendingContacts) {
      selectionMenu = (
        <EllipsisMenu
          behavior="button-dropdown"
          compact
          options={this._menuOptions()}
          disabled={selectedNum === 0}
          onOptionClick={this._onActionClick}
        />
      )
    }

    return (
      <>
        <PageHeader
          title={list ? <span translate="no">{list.name}</span> : 'Contacts'}
          actions={
            <ButtonContainer>
              {addBtn}
              {!list && (
                <EllipsisMenu
                  compact
                  icon="ellipsis"
                  options={[
                    {
                      disabled:
                        !this.props.isAdmin || filteredContacts.length === 0,
                      label: 'Export all contacts to CSV',
                      nodeRight: !this.props.isAdmin && (
                        <Tooltip content="Only admins can export contacts.">
                          <Icon type="info-outline" width={16} height={16} />
                        </Tooltip>
                      ),
                      onClick: () => this._exportClick(),
                    },
                    {
                      label: CAPTION_IMPORT,
                      onClick: () =>
                        this.props.navigate('/contacts/list/file-import'),
                    },
                  ]}
                  hasArrow={false}
                />
              )}
              {listMenu}
            </ButtonContainer>
          }
        />
        <PageContainer>
          {optinModal}
          {warningContact && (
            <ModalDialog
              title="Contacts could not be added"
              primaryAction={{
                caption: 'OK',
                onClick: () => {
                  this.props.removeWarning()
                },
              }}
              status="warning"
            >
              Contacts without email, like <b>“{warningContact.name}”</b>, could
              not be added.
            </ModalDialog>
          )}
          {this._renderModal()}
          <>
            {appendingContacts && (
              <div className="ContactsList_ExistingTip">
                Choose existing contacts from the list below.
              </div>
            )}
            <div className="ContactsList_MenuBar">
              <Input
                disabled={!enableSearch}
                icon="search"
                value={this.state.searchTerm}
                placeholder={searchPlaceholder}
                onChange={this._onSearch}
              />
              {appendingContacts && list && (
                <Link
                  className="ContactsList_BackLink"
                  to={`/lists/${list.id}`}
                >
                  Back to Mailing List
                </Link>
              )}
              <div className="ContactsList_MenuSpacer" />
              <div
                className={cn('ContactsList_MenuSelected', {
                  'ContactsList_MenuSelected--Hidden': selectedNum === 0,
                })}
              >
                <div className="ContactsList_MenuSelected_Caption">
                  {selectedNum} selected
                </div>
                {selectionMenu}
              </div>
            </div>

            <ContactsTable
              headers={[
                {
                  label: 'Name',
                  value: 'Name',
                },
                {
                  label: 'Email',
                  value: 'Email',
                },
                {
                  label: 'Company',
                  value: 'Company',
                },
                !this.props.list && {
                  label: 'Phone',
                  value: 'Phone',
                },
                {
                  label: 'Location',
                  value: 'Territory',
                },
                {
                  label: 'Tags',
                  value: 'Tags',
                },
                !!this.props.list && {
                  label: 'Status',
                  value: 'Status',
                },
                {
                  label: 'Edit',
                  value: 'Action',
                },
              ]}
              columns={[
                {
                  label: '',
                  value: 'Avatar',
                  renderData: data => (
                    <>
                      <Checkbox
                        className="ContactCell_Checkbox"
                        checked={data.selected || false}
                        onChange={() => this._onSelect(data)}
                      />
                      <UserAvatar
                        name={data.name || data.email}
                        size="1.25rem"
                        fontSize=".6rem"
                      />
                    </>
                  ),
                },
                {
                  id: 'name',
                  label: 'Name',
                  value: 'Name',
                  editable: true,
                  batchEditable: false,
                },
                {
                  id: 'email',
                  label: 'Email',
                  value: 'Email',
                  editable: true,
                  batchEditable: false,
                },
                {
                  id: 'company',
                  label: 'Company',
                  value: 'Company',
                  editable: true,
                },
                !this.props.list && {
                  id: 'phone',
                  label: 'Phone',
                  value: 'Phone',
                  editable: true,
                },
                {
                  id: 'territory',
                  label: 'Location',
                  value: 'Territory',
                  editable: true,
                },
                {
                  label: 'Tags',
                  value: 'Tags',
                  renderData: data => (
                    <ContactsTagList
                      contact={data}
                      onFieldChange={this._onFieldChange}
                      onFieldValueAdd={this._onFieldValueAdd}
                      onFieldValueRemove={this._onFieldValueRemove}
                      updateContactsTags={updateContactsTags}
                    />
                  ),
                },
                !!this.props.list && {
                  label: 'Status',
                  value: 'Status',
                  renderData: data => {
                    const contactStatus = getContactStatus(
                      data,
                      this.props.list && this.props.list.id,
                    )
                    let unsubscribeTooltip

                    switch (contactStatus) {
                      case '2_UNSUBSCRIBE_ALL':
                        unsubscribeTooltip = 'Unsubscribed from all emails'
                        break

                      case '1_UNSUBSCRIBED_THIS':
                        unsubscribeTooltip = 'Unsubscribed from this list'
                        break

                      case '0_SUBSCRIBED':
                        unsubscribeTooltip = 'Subscribed'
                        break
                    }

                    return (
                      <HelpTooltip
                        base={
                          <div className="ContactCell_UnsubIcon">
                            <UnsubscribeIcon value={contactStatus} />
                          </div>
                        }
                      >
                        {unsubscribeTooltip}
                      </HelpTooltip>
                    )
                  },
                },
              ]}
              contacts={filteredContacts}
              sortContacts={this._sortContacts}
              sortParam={sortParam}
              sortDirection={sortDirection}
              searchTerm={searchTerm}
              list={this.props.list}
              appendingContacts={appendingContacts}
              onRowModeSwitch={this._onRowModeSwitch}
              onFieldChange={this._onFieldChange}
              onFieldValueAdd={this._onFieldValueAdd}
              onFieldValueRemove={this._onFieldValueRemove}
              onSelect={this._onSelect}
              onSelectAll={this._selectAll}
              loading={loading}
              loaded={loaded}
              error={error}
              contactsCount={ids.length}
              loadContacts={loadNextContacts}
            />
          </>
          <Outlet />
        </PageContainer>
      </>
    )
  }
}

const ConnectedContactsList = connector(ContactsList)

function ContactsListWithNavigate(props) {
  const navigate = useNavigate()
  return <ConnectedContactsList {...props} navigate={navigate} />
}

export default ContactsListWithNavigate
